import { type PatSnapCommonResponseData, PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface OcrSubmitResult {
    job_id: string;
}

export interface OcrResult {
    jobId: string;
    status: 'PENDING' | 'SUCCESS' | 'FAILED';
    content?: string;
}

export interface AvailableAgent {
    agent_id: string;
    user_pin: boolean;
    pay_type: string;
}

export interface AvailableAgentsData {
    pin_list: AvailableAgent[];
    full_list: AvailableAgent[];
}

export interface ImageUploadResult {
    file_id: string;
}

export interface ClawHomeApi {
    submitOcr(file: File): Promise<OcrSubmitResult>;
    getOcrResult(jobId: string): Promise<OcrResult>;
    getAvailableAgents(): Promise<AvailableAgentsData>;
    pinAgents(agentIds: string[]): Promise<boolean>;
    getUserMonitorList(email?: string): Promise<any[]>;
    uploadImage(file: File): Promise<ImageUploadResult>;
}

export const ClawHomeApi = Symbol('ClawHomeApi');

@injectable()
export class ClawHomeApiImpl extends PsHttpService implements ClawHomeApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka`,
            baseURL2: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka`,
        };
    }

    getAvailableAgents(): Promise<AvailableAgentsData> {
        return this.get<PatSnapCommonResponseData<AvailableAgentsData>>({
            url: `${this.options.baseURL2}/internal/user/available-agents`,
            headers: { 'X-Api-Version': '1.0' },
        }).then((res) => {
            return res.data as AvailableAgentsData;
        });
    }

    pinAgents(agentIds: string[]): Promise<boolean> {
        return this.post<PatSnapCommonResponseData<boolean>>({
            url: `${this.options.baseURL2}/internal/user/pin-agent`,
            headers: { 'X-Api-Version': '1.0' },
            data: { agent_ids: agentIds },
        }).then((res) => {
            return res.data as boolean;
        });
    }

    submitOcr(file: File): Promise<OcrSubmitResult> {
        const formData = new FormData();
        formData.append('file', file);
        return this.post<PatSnapCommonResponseData<OcrSubmitResult>>({
            url: `${this.options.baseURL2}/internal/file/ocr/submit`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then((res) => {
            return res.data as OcrSubmitResult;
        });
    }

    getOcrResult(jobId: string): Promise<OcrResult> {
        return this.get<PatSnapCommonResponseData<OcrResult>>({
            url: `${this.options.baseURL2}/internal/file/ocr/result/${jobId}`,
        }).then((res) => {
            return res.data as OcrResult;
        });
    }

    getUserMonitorList(email?: string): Promise<any[]> {
        return this.get<PatSnapCommonResponseData<any[]>>({
            url: `${this.options.baseURL2}/intelligence/brief/list`,
            params: { email },
        }).then((res) => {
            return res.data as any[];
        });
    }

    uploadImage(file: File): Promise<ImageUploadResult> {
        const formData = new FormData();
        formData.append('file', file);
        return this.post<PatSnapCommonResponseData<ImageUploadResult>>({
            url: `${this.options.baseURL}/image/upload`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then((res) => {
            return res.data as ImageUploadResult;
        });
    }
}
